import * as Yup from "yup";

import {
  EMPTY_ADDRESS,
  EMPTY_CONTACT,
  EMAIL_PATTERN,
  MIN_REQUIRED_SELECTION_COUNT,
  TENANT_VALIDATION_MESSAGES,
  TENANT_YUP_TEST_NAMES,
} from "constants/constants";
import {
  buildAddressPayload,
  buildContactPayload,
  createAddressSchema,
  createContactSchema,
  createGstinSchema,
  createTaxRegistrationNumberSchema,
  findOptionById,
  getAddressFormFlagsForCountry,
  getIsdCodeOptionsForCountry,
  normalizeAddressFields,
  normalizeUuid,
  resetIncompatibleTaxFieldsForCountry,
} from "helpers/tenantUtils";

export const BRANCH_FORM_INITIAL_VALUES = {
  name: "",
  email: "",
  gstin: null,
  taxRegistrationNumber: "",
  ambassadorMail: "",
  gradeIds: [],
  address: { ...EMPTY_ADDRESS },
  contact: { ...EMPTY_CONTACT },
};

export const normalizeBranchInitialValues = (
  branchDetails,
  { countryOptions = [], tenantCountryId } = {},
) => {
  const selectedCountry = findOptionById(countryOptions, tenantCountryId);
  const { isIndia } = getAddressFormFlagsForCountry(selectedCountry);
  const isdCodeOptions = getIsdCodeOptionsForCountry(
    countryOptions,
    tenantCountryId,
  );
  const rawIsdCode =
    branchDetails?.contact?.isdCode ||
    branchDetails?.contact?.isd_code ||
    "";

  return resetIncompatibleTaxFieldsForCountry(
    {
      name: branchDetails?.branchName,
      email: branchDetails?.email,
      gstin: branchDetails?.gstin || "",
      taxRegistrationNumber:
        branchDetails?.taxRegistrationNumber ||
        branchDetails?.tax_registration_number ||
        "",
      ambassadorMail: branchDetails?.ambassadorMail || "",
      gradeIds: branchDetails?.gradeIds,
      lockedGradeIds: branchDetails?.gradeIds,
      address: normalizeAddressFields(branchDetails?.address),
      contact: branchDetails?.contact
        ? {
            ...branchDetails.contact,
            isdCode: findOptionById(isdCodeOptions, rawIsdCode)
              ? rawIsdCode
              : "",
          }
        : { ...EMPTY_CONTACT },
    },
    { isIndia: selectedCountry ? isIndia : undefined },
  );
};

export const buildBranchPayload = (
  values,
  countryOptions = [],
  tenantCountryId,
  branchId = null,
  tenantId = null,
) => {
  const {
    name,
    email,
    gstin,
    taxRegistrationNumber,
    ambassadorMail,
    gradeIds,
    address,
    contact,
  } = values;

  const payload = {
    name,
    email,
    gstin: gstin?.trim() ? gstin : null,
    tax_registration_number: taxRegistrationNumber?.trim()
      ? taxRegistrationNumber.trim()
      : null,
    ambassador_mail: ambassadorMail?.trim() ? ambassadorMail : null,
    grade_ids: gradeIds.map(Number),
    address: buildAddressPayload(address, countryOptions, tenantCountryId),
    contact: buildContactPayload(
      contact,
      getIsdCodeOptionsForCountry(countryOptions, tenantCountryId),
    ),
  };

  if (branchId != null) {
    payload.id = normalizeUuid(branchId);
  }

  if (tenantId != null) {
    payload.tenant_id = normalizeUuid(tenantId);
  }

  return payload;
};

export const createBranchSchema = (countryOptions, tenantCountryId) => {
  const getCountryId = () => tenantCountryId;

  return Yup.object().shape({
    name: Yup.string().required(TENANT_VALIDATION_MESSAGES.BRANCH_NAME_REQUIRED),
    email: Yup.string()
      .email(TENANT_VALIDATION_MESSAGES.EMAIL_INVALID)
      .required(TENANT_VALIDATION_MESSAGES.EMAIL_REQUIRED),
    gstin: createGstinSchema(),
    taxRegistrationNumber: createTaxRegistrationNumberSchema(),
    ambassadorMail: Yup.string()
      .email(TENANT_VALIDATION_MESSAGES.EMAIL_INVALID)
      .nullable()
      .notRequired()
      .test(
        TENANT_YUP_TEST_NAMES.AMBASSADOR_EMAIL,
        TENANT_VALIDATION_MESSAGES.EMAIL_INVALID,
        (value) => (!value ? true : EMAIL_PATTERN.test(value)),
      ),
    gradeIds: Yup.array()
      .of(Yup.number())
      .min(MIN_REQUIRED_SELECTION_COUNT, TENANT_VALIDATION_MESSAGES.GRADE_MIN),
    address: Yup.object().shape(
      createAddressSchema(countryOptions, getCountryId),
    ),
    contact: createContactSchema(countryOptions, getCountryId),
  });
};
